import { Dialog, DialogActions } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCompanyLogo } from '../../../../../../../../lib/api/company';
import { getConfirmingRequestById } from '../../../../../../../../lib/api/confirming';
import { fetchDetails as getCreditRequestDetails } from '../../../../../../../../lib/api/creditRequest';
import { expressGetCompanyLogo } from '../../../../../../../../lib/api/express';
import { getCompanyPayoutGroup } from '../../../../../../../../lib/api/payments';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import { cellFormat } from '../../../../../../../../lib/designSystem/Table/cellFormat';
import EnhancedTab from '../../../../../../../../lib/designSystem/TabSimulation';
import { showAlert } from '../../../../../../../../lib/functions/utils';
import { generateSimulationPDF } from '../../../../../../../../pages/Finance/Components/Financing/common/exportSimulation';
import { generateConfirmingSimulationPDF } from '../../../../../../../../pages/Finance/Components/Financing/common/exportSimulationConfirming';
import { generateSimulationCreditPDF } from '../../../../../../../../pages/Finance/Components/Financing/common/exportSimulationCredit';
import { AuthState } from '../../../../../../../../services/redux/authSlice';
import PayOutDetailAppliedDocuments from './components/AppliedDocuments';
import PayOutDetailDocuments from './components/Documents';
import PayOutDetailPayouts from './components/Payouts';
import PayOutDetaiPendingAccounts from './components/PendingAccounts';
import PayOutSummary from './components/Summary';
import PayOutSummaryConfirming from './components/SummaryConfirming';

const tabsBySource = (
  request: any,
  setSuccess: any,
  setFailure: any,
  reset: any,
  onExport: () => void,
  setActiveTab: (tab: number) => void,
  setUp: any,
  refreshPaymentGroupDetails: any,
) => {
  const { source } = request;

  const confirmingSummary = {
    index: 0,
    label: 'Resumen',
    component: (
      <PayOutSummaryConfirming request={request} onExport={onExport} />
    ),
  };

  const summary = {
    index: 0,
    label: 'Resumen',
    component: <PayOutSummary request={request} onExport={onExport} />,
  };

  const payments = {
    index: 0,
    label: 'Pagos',
    component: (
      <PayOutDetailPayouts
        request={request}
        payments={request.payments}
        sourceId={request.sourceId}
        source={request.source}
        setSuccess={setSuccess}
        setFailure={setFailure}
        reset={reset}
        setActiveTab={setActiveTab}
        setUp={setUp}
        refreshPaymentGroupDetails={refreshPaymentGroupDetails}
      />
    ),
  };

  const appliedDocuments = {
    index: 1,
    label: 'Documentos Aplicados',
    component: (
      <PayOutDetailAppliedDocuments
        documentsApplied={request.documentsApplied}
        source={request.source}
        sourceId={request.sourceId}
        setSuccess={setSuccess}
        setFailure={setFailure}
        reset={reset}
      />
    ),
  };

  const documents = {
    index: 2,
    label: 'Documentos',
    component: (
      <PayOutDetailDocuments
        documents={request.documents}
        source={request.source}
        sourceId={request.sourceId}
        setSuccess={setSuccess}
        setFailure={setFailure}
        reset={reset}
      />
    ),
  };

  const pendingAccouns = {
    index: 3,
    label: 'Cuentas pendientes',
    component: (
      <PayOutDetaiPendingAccounts
        pendingAccounts={request.pendingAccounts}
        source={request.source}
        sourceId={request.sourceId}
        setSuccess={setSuccess}
        setFailure={setFailure}
        reset={reset}
      />
    ),
  };

  switch (source) {
    case 'factoring-request':
      return [summary, payments];
    case 'confirming-request':
      return [confirmingSummary, payments];
    case 'payment-request':
      return [summary, documents, payments];
    case 'payment-request-passive':
      return [summary, pendingAccouns, appliedDocuments, payments];
    case 'credit-request':
      return [summary, payments];
    default:
      return [];
  }
};

const PayOutDetails = (props: any) => {
  const {
    paymentGroupedDetails,
    open,
    setOpen,
    setSuccess,
    setFailure,
    setUp,
    refreshPaymentGroupDetails,
  } = props;
  const startValue = 0;
  const [activeTab, setActiveTab] = useState(startValue);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const reset = async () => {
    await setUp();
    setOpen(false);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      // Map source types to the correct format for the API
      const sourceTypeMap: { [key: string]: string } = {
        'factoring-request': 'factoring-request',
        'confirming-request': 'confirming-request',
        'credit-request': 'credit-request',
        'payment-request': 'payment-request',
        'payment-request-passive': 'payment-request-passive',
      };

      const sourceType =
        sourceTypeMap[paymentGroupedDetails.source] || 'factoring-request';

      // Get payouts data
      const payouts = await getCompanyPayoutGroup(
        dispatch,
        sourceType,
        paymentGroupedDetails.sourceId,
      );

      // Get additional request details for confirming and credit requests (similar to factoring)
      let additionalRequestData = {};
      if (paymentGroupedDetails.source === 'confirming-request') {
        try {
          additionalRequestData = await getConfirmingRequestById(
            dispatch,
            paymentGroupedDetails.sourceId,
          );
        } catch (error) {
          console.error('Error getting confirming request details:', error);
        }
      } else if (paymentGroupedDetails.source === 'credit-request') {
        try {
          additionalRequestData = await getCreditRequestDetails(
            dispatch,
            paymentGroupedDetails.sourceId,
          );
        } catch (error) {
          console.error('Error getting credit request details:', error);
        }
      }

      // Get company logo
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);

      // Merge additional request data with paymentGroupedDetails
      const mergedData = { ...paymentGroupedDetails, ...additionalRequestData };

      // Build request object for PDF generation
      const request = {
        id: mergedData.sourceId || mergedData.id,
        executiveName: user?.name || '',
        businessIdentifier:
          mergedData.businessIdentifier ||
          mergedData.stakeholderIdentifier ||
          '',
        businessName:
          mergedData.businessName || mergedData.stakeholderName || 'N/A',
        subProductName:
          mergedData.sourceTranslation || mergedData.subProductName || 'N/A',
        createdAt: new Date(),
        operationDate:
          mergedData.createdAt || mergedData.operationDate || new Date(),
        financingAt:
          mergedData.createdAt || mergedData.financingAt || new Date(),
        amount: mergedData.amount || mergedData.amountToDepositTotal || 0,
        amountToDepositTotal: mergedData.amountToDepositTotal || 0,
        // Handle documents differently based on request type - now with merged data
        documents: mergedData.documents || mergedData.documentsApplied || [],
        // Exclude documentsToApply to prevent aplicaciones section from appearing
        documentsToApply: [],
        payments: mergedData.payments || [],
        source: mergedData.source || '',
        sourceTranslation:
          mergedData.sourceTranslation || mergedData.subProductName || 'N/A',
        // Add other required fields with defaults
        advance: mergedData.advance,
        rate: mergedData.rate,
        arrearRate: mergedData.arrearRate || 0,
        amountToApply: mergedData.amountToApply,
        expenses: mergedData.expenses.reduce(
          (
            acc: number,
            expense: { name: string; amount: number; hasIva: boolean },
          ) => acc + expense.amount,
          0,
        ),
        operationCostFixed: mergedData.operationCostFixed,
        operationCostVariable: mergedData.operationCostVariable,
        operationCostIva: mergedData.operationCostIva || 0,
        expensesCostIva: mergedData.expenses.reduce(
          (
            acc: number,
            expense: { name: string; amount: number; hasIva: boolean },
          ) => acc + (expense.hasIva ? expense.amount * 0.19 : 0),
          0,
        ),
        expensesDetails: mergedData.expenses,
        guaranteeAmount: mergedData.guaranteeAmount,
        guaranteeObservation: mergedData.guaranteeObservation || '',
        showAdvance: true,
        showRate: true,
        showArrearRate: true,
        showAmount: true,
        showAmountAdvance: true,
        showAmountRetained: true,
        showAmountDifference: true,
        showAmountComissionIva: true,
        showAmountTotalApplied: true,
        showDraftBalance: true,
        showExpenses: true,
        showGuaranteeAmount: true,
        showExpensesDetails: true,
      };

      if (paymentGroupedDetails.source === 'confirming-request') {
        // Build confirming-specific request object
        const confirmingRequest = {
          ...mergedData,
          showClientInfo: true,
          showRequestInfo: true,
          showDates: true,
          showAdvance: true,
          showRate: true,
          showArrearRate: true,
          showAmounts: true,
          showDocuments: true,
          showOperationCosts: true,
          showExpenses: true,
          showAccountReceivable: true,
        };
        await generateConfirmingSimulationPDF(
          confirmingRequest,
          user?.name || '',
          imageBase64,
          payouts,
        );
      } else if (paymentGroupedDetails.source === 'credit-request') {
        // Build credit-specific request object
        const creditRequest = {
          id: mergedData.sourceId || mergedData.id,
          businessIdentifier:
            mergedData.businessIdentifier ||
            mergedData.stakeholderIdentifier ||
            '',
          businessName:
            mergedData.businessName || mergedData.stakeholderName || 'N/A',
          creditType: 'Crédito de Consumo', // Default type
          interestType: 'Fijo', // Default type
          startDate: mergedData.createdAt
            ? new Date(mergedData.createdAt).toLocaleDateString('es-CL')
            : new Date().toLocaleDateString('es-CL'),
          firstPaymentDate: mergedData.createdAt
            ? new Date(mergedData.createdAt).toLocaleDateString('es-CL')
            : new Date().toLocaleDateString('es-CL'),
          lastPaymentDate: mergedData.createdAt
            ? new Date(mergedData.createdAt).toLocaleDateString('es-CL')
            : new Date().toLocaleDateString('es-CL'),
          months: 12, // Default
          totalPayments: 12, // Default
          paymentDay: 1, // Default
          totalAmount:
            mergedData.amount || mergedData.amountToDepositTotal || 0,
          operationAmount: 0,
          insuranceAmount: 0,
          totalLoanAmount:
            mergedData.amount || mergedData.amountToDepositTotal || 0,
          totalCost: mergedData.amount || mergedData.amountToDepositTotal || 0,
          rate: 0,
          anualRate: 0,
          arrearRate: 0,
          anualArrearRate: 0,
          cae: 0,
          warranties: [],
          amortizationSchedule: [],
          documents: mergedData.documents || mergedData.documentsApplied || [],
          showAmount: true,
          showOperationExpenses: true,
          showInsurance: true,
          showTotalCredit: true,
          showCreditCost: true,
          showMonthlyInterest: true,
          showAnnualInterest: true,
          showArrearInterest: true,
          showAnnualArrearInterest: true,
          showCAE: true,
          showWarranties: true,
          showPaymentTable: true,
        };
        await generateSimulationCreditPDF(
          creditRequest,
          user?.name || '',
          imageBase64,
          payouts,
        );
      } else {
        // Default to factoring for factoring-request and other types+
        await generateSimulationPDF(
          request,
          user?.name || '',
          imageBase64,
          payouts,
        );
      }

      showAlert(dispatch, 'success', 'Pagos exportados correctamente');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al exportar pagos');
    }
    setLoading(false);
  };

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const tabs = tabsBySource(
    paymentGroupedDetails,
    setSuccess,
    setFailure,
    reset,
    handleExport,
    setActiveTab, // Pass the setActiveTab function to switch tabs after completion
    setUp, // Pass the setUp function to refresh data without closing dialog
    refreshPaymentGroupDetails, // Pass the function to refresh payment group details
  );

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={() => setOpen(false)}>
      <div style={{ padding: 20, minHeight: 200 }}>
        <EnhancedTab
          title={`${paymentGroupedDetails.sourceTranslation} ${cellFormat(paymentGroupedDetails.sourceId, 'numeric-id')} `}
          startValue={activeTab}
          tabs={tabs}
          onTabChange={handleTabChange}
        />
      </div>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          {' '}
          Salir{' '}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default PayOutDetails;
